import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import api, { formatPrice } from '../api';
import { useToast } from '../hooks/use-toast';
import { Minus, Plus, X, Smartphone, Banknote } from 'lucide-react';

const loadRazorpay = () => new Promise((resolve, reject) => {
  if (window.Razorpay) return resolve();
  const s = document.createElement('script');
  s.src = 'https://checkout.razorpay.com/v1/checkout.js';
  s.onload = resolve;
  s.onerror = () => reject(new Error('Could not load payment gateway'));
  document.body.appendChild(s);
});

const PayOption = ({ id, active, onSelect, icon: Icon, title, desc, testId }) => (
  <button type="button" data-testid={testId} onClick={() => onSelect(id)} className={`w-full text-left border p-4 flex items-center gap-4 transition-colors ${active ? 'border-[#C6410E] bg-[#E9E1D4]/40' : 'border-black/15 hover:border-black/40'}`}>
    <span className={`w-3 h-3 rounded-full border shrink-0 ${active ? 'bg-[#C6410E] border-[#C6410E]' : 'border-black/40'}`} />
    <Icon className="w-5 h-5 shrink-0" />
    <div>
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-[#1a1a1a]/60">{desc}</p>
    </div>
  </button>
);

export default function Checkout() {
  const { items, setQty, remove, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [payment, setPayment] = useState('cod');
  const [rzp, setRzp] = useState({ razorpay_enabled: false });
  const [shipping, setShipping] = useState({ name: '', phone: '', email: '', address: '', city: '', pincode: '', notes: '' });

  useEffect(() => {
    api.get('/payments/config').then(r => { setRzp(r.data); if (r.data.razorpay_enabled) setPayment('razorpay'); }).catch(() => {});
  }, []);

  const shipFee = subtotal >= 5000 || subtotal === 0 ? 0 : 200;
  const total = subtotal + shipFee;

  const openRazorpay = (order) => new Promise((resolve, reject) => {
    const r = order.razorpay;
    const options = {
      key: r.key_id, amount: r.amount, currency: r.currency, order_id: r.order_id,
      name: 'barebasics', description: `Order ${order.id}`,
      prefill: { name: shipping.name, email: shipping.email, contact: shipping.phone },
      theme: { color: '#C6410E' },
      handler: (res) => resolve(res),
      modal: { ondismiss: () => reject(new Error('cancelled')) },
    };
    const instance = new window.Razorpay(options);
    instance.on('payment.failed', (res) => reject(new Error(res.error?.description || 'Payment failed')));
    instance.open();
  });

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post('/orders', { items, shipping, payment_method: payment });
      if (payment === 'cod') {
        clear();
        navigate('/order-success', { state: { order: data } });
        return;
      }
      await loadRazorpay();
      const res = await openRazorpay(data);
      const verified = await api.post('/payments/razorpay/verify', {
        order_id: data.id,
        razorpay_order_id: res.razorpay_order_id,
        razorpay_payment_id: res.razorpay_payment_id,
        razorpay_signature: res.razorpay_signature,
      });
      clear();
      navigate('/order-success', { state: { order: verified.data } });
    } catch (err) {
      if (err.message === 'cancelled') {
        toast({ title: 'Payment cancelled', description: 'Your bag is saved. You can try again or choose Cash on Delivery.' });
      } else {
        toast({ title: 'Order failed', description: err.response?.data?.detail || err.message || 'Please try again' });
      }
    } finally { setBusy(false); }
  };

  if (items.length === 0) {
    return (
      <main className="max-w-3xl mx-auto px-6 py-24 text-center">
        <p className="text-[11px] tracking-widest-plus uppercase text-[#C6410E] mb-4">Checkout</p>
        <h1 className="font-serif-display text-5xl mb-6">Your bag is empty.</h1>
        <p className="text-[#1a1a1a]/70 mb-8">Add a few considered pieces from the collection, then return here.</p>
        <Link to="/shop" className="btn-primary">Explore the collection</Link>
      </main>
    );
  }

  return (
    <main className="max-w-7xl mx-auto px-6 py-16">
      <p className="text-[11px] tracking-widest-plus uppercase text-[#C6410E] mb-4">Checkout</p>
      <h1 className="font-serif-display text-5xl mb-12">Almost there.</h1>

      <div className="grid md:grid-cols-3 gap-12">
        {/* Form */}
        <form onSubmit={submit} className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
          <h2 className="font-serif-display text-2xl md:col-span-2 border-b border-black/10 pb-3">Shipping details</h2>
          {[
            ['name', 'Full name', 1],
            ['email', 'Email', 1, 'email'],
            ['phone', 'Phone', 1, 'tel'],
            ['pincode', 'Pincode', 1],
            ['address', 'Address', 2],
            ['city', 'City', 1],
          ].map(([k, l, span, type]) => (
            <div key={k} className={span === 2 ? 'md:col-span-2' : ''}>
              <label className="text-[11px] tracking-widest-plus uppercase block mb-2">{l}</label>
              <input required type={type || 'text'} value={shipping[k]} onChange={e => setShipping({...shipping, [k]: e.target.value})} className="w-full bg-transparent border-b border-black/30 focus:border-[#C6410E] outline-none py-2 text-sm transition-colors" />
            </div>
          ))}
          <div className="md:col-span-2">
            <label className="text-[11px] tracking-widest-plus uppercase block mb-2">Notes (optional)</label>
            <textarea rows={3} value={shipping.notes} onChange={e => setShipping({...shipping, notes: e.target.value})} className="w-full bg-transparent border-b border-black/30 focus:border-[#C6410E] outline-none py-2 text-sm resize-none" />
          </div>

          <h2 className="font-serif-display text-2xl md:col-span-2 border-b border-black/10 pb-3 mt-4">Payment</h2>
          <div className="md:col-span-2 space-y-3">
            {rzp.razorpay_enabled && (
              <PayOption id="razorpay" testId="pay-option-razorpay" active={payment === 'razorpay'} onSelect={setPayment} icon={Smartphone}
                title="Pay online — UPI, Cards, NetBanking" desc="Secure payment via Razorpay. Instant confirmation." />
            )}
            <PayOption id="cod" testId="pay-option-cod" active={payment === 'cod'} onSelect={setPayment} icon={Banknote}
              title="Cash on Delivery" desc={rzp.razorpay_enabled ? 'Pay when your order arrives.' : 'Pay when your order arrives. Online payment coming soon.'} />
          </div>
          <button data-testid="place-order-btn" disabled={busy} className="btn-primary md:col-span-2 mt-4">
            {busy ? (payment === 'razorpay' ? 'Opening secure payment…' : 'Placing your order…') : (payment === 'razorpay' ? `Pay ${formatPrice(total)}` : `Place order — ${formatPrice(total)}`)}
          </button>
        </form>

        {/* Summary */}
        <aside className="bg-[#E9E1D4] p-6 h-fit">
          <h2 className="font-serif-display text-2xl mb-6">Your bag</h2>
          <ul className="space-y-5 mb-6">
            {items.map(it => (
              <li key={it.product_id} className="flex gap-3">
                <div className="w-16 h-20 overflow-hidden bg-[#F4EEE4] shrink-0">
                  <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{it.name}</p>
                  <p className="text-xs text-[#1a1a1a]/60 mt-1">{formatPrice(it.price)}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <button onClick={() => setQty(it.product_id, it.qty - 1)} className="w-6 h-6 border border-black/30 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                    <span className="text-xs w-5 text-center">{it.qty}</span>
                    <button onClick={() => setQty(it.product_id, it.qty + 1)} className="w-6 h-6 border border-black/30 flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                    <button onClick={() => remove(it.product_id)} className="ml-auto text-[#1a1a1a]/50"><X className="w-4 h-4" /></button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <div className="divider-thin mb-4" />
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{formatPrice(subtotal)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span>{shipFee === 0 ? 'Free' : formatPrice(shipFee)}</span></div>
            <div className="flex justify-between font-medium text-base pt-2 border-t border-black/10"><span>Total</span><span>{formatPrice(total)}</span></div>
          </div>
        </aside>
      </div>
    </main>
  );
}
